import { useContext } from 'react';

import Cookies from 'js-cookie';

import { COOKIE_NAMES } from '../constants/cookies';
import { routeToFeatureMap } from '../constants/featureMap';
import { nonCurriculumRoutes } from '../constants/routes';
import PortalContext from '../context/portal/context';
import { PortalLink } from '../enums/portal.enum';
import { MenuItemType } from '../types/menu-item';
import { getFeaturePath, getFirstPathSegment } from '../utils/pathUtils';

const normalizePath = (path: string, selectedPortal?: string): string => {
  // Extract the feature path (without portal prefix if it exists)
  const featurePath = getFeaturePath(path);
  const firstSegment = getFirstPathSegment(path);
  const feature = routeToFeatureMap[featurePath];

  if (!feature) return path; // Return original if no feature found

  const isNonCurriculum = nonCurriculumRoutes.some(
    (route) => route.featureKey === feature,
  );

  const hasPortalPrefix =
    firstSegment &&
    Object.values(PortalLink).some((p) => p === `/${firstSegment}`);

  const portalPrefix = hasPortalPrefix
    ? `/${firstSegment}`
    : PortalLink[Cookies.get(COOKIE_NAMES.SELECTED_PORTAL) || selectedPortal];

  // Create the normalized path as stored in localStorage
  return isNonCurriculum
    ? `${featurePath}`
    : `${portalPrefix}${featurePath}`;
};

const togglePinnedApp = (path: string): void => {
  if (typeof window === 'undefined') return;

  const storedPinnedApps = localStorage.getItem('pinnedApps') || '';
  const pinnedApps = storedPinnedApps ? storedPinnedApps.split(',') : [];

  const normalizedPath = normalizePath(path);

  // Check if the path is already pinned and remove it if it is
  if (pinnedApps.includes(normalizedPath)) {
    const filteredApps = pinnedApps.filter((app) => app !== normalizedPath);
    localStorage.setItem('pinnedApps', filteredApps.join(','));
  } else {
    pinnedApps.push(normalizedPath);
    localStorage.setItem('pinnedApps', pinnedApps.join(','));
  }
  window.dispatchEvent(new Event('pinnedAppsChanged'));
};

const useStorePinnedApps = () => {
  const { selectedPortal } = useContext(PortalContext);

  const isItemPinned = (item: MenuItemType, pinnedApps: MenuItemType[]): boolean => {
    const normalizedItemPath = normalizePath(item.href, selectedPortal);
    return pinnedApps.some(pinnedApp => normalizePath(pinnedApp.href, selectedPortal) === normalizedItemPath);
  };

  return {
    togglePinnedApp,
    isItemPinned,
    normalizePath: (path: string) => normalizePath(path, selectedPortal)
  };
};

export default useStorePinnedApps;
